import dayjs from 'dayjs'
import { computed, inject } from 'vue'

import constants from '@/config/constants'
import $store from '@/store/index'

export function useTrialPeriod () {
  const $auth = inject('$auth')
  const $config = inject('$config')

  // set per user by support through the CRM; the config value is only the fallback for accounts
  // that have no value stored. See server/TRIAL-PERIOD.md
  const trialPeriodDurationInDays = computed(() => {
    return $auth.user.payment.trialperioddays ?? $config.trialPeriodDurationInDays
  })

  const trialPeriodEndDate = computed(() => {
    return dayjs($auth.user.payment.firstproject)
      .add(trialPeriodDurationInDays.value, 'days')
  })

  const daysToTrialEnd = computed(() => {
    return dayjs()
      .to(trialPeriodEndDate.value)
  })

  const didTrialPeriodEnd = computed(() => {
    return dayjs() > trialPeriodEndDate.value
  })

  const isTrialActive = computed(() => {
    const plan = $auth.user.payment.customerbillingplan.plan

    const hasPlanSelected = plan && plan !== constants.BILLING_PLANS.NONE

    const hasFirstProject = $auth.user.payment.firstproject

    return !hasPlanSelected && hasFirstProject && $auth.user.crm.numberOfProjects > 0
  })

  const userHasFilledPaymentDataByHimself = computed(() => {
    return Boolean($auth.user.payment && $auth.user.payment.enabled)
  })

  return {
    trialPeriodDurationInDays,
    daysToTrialEnd,
    didTrialPeriodEnd,
    isTrialActive,
    userHasFilledPaymentDataByHimself,
  }
}

export function useShowBannerTrialPeriod () {
  const $auth = inject('$auth')
  const $config = inject('$config')
  const { trialPeriodDurationInDays, isTrialActive, didTrialPeriodEnd, daysToTrialEnd, userHasFilledPaymentDataByHimself } = useTrialPeriod()

  const hasRegisteredBefore2016 = computed(() => {
    return $auth.user.date && dayjs($auth.user.date) < dayjs('2016-01-01')
  })

  // the banner is a 10 day warning ahead of the trial end, whatever the trial's length
  const hasProjectOlderThanBannerThreshold = computed(() => {
    return $auth.user.payment.firstproject && dayjs()
      .diff($auth.user.payment.firstproject, 'days') > trialPeriodDurationInDays.value - 10
  })

  const showBanner = computed(() => {
    if ($store.state.project.showTwoFactorAuthBlockedScreen) return false

    if ($config.isBackupApp) return false

    if ($config.customerFeatures.account.disablePayment) return false

    if ($config.customerFeatures.hideFeatureAlerts) return false

    if ($auth.user.crm.isCompanyWideAccount) return false

    if (hasRegisteredBefore2016.value) return false

    if (userHasFilledPaymentDataByHimself.value) return false

    if (!isTrialActive.value) return false

    return hasProjectOlderThanBannerThreshold.value
  })

  return {
    showBanner,
    didTrialPeriodEnd,
    daysToTrialEnd,
  }
}
